<template>

  <div class="dashboard-container">
    <side-bar />
    <div class="main-content">
      <!--Breadcrumbs-->

      <div class="container">
        <div class="tm-grey-text tm-breadcrumbs">
          <router-link to="/partner/dashboard" class="hover:underline">Home</router-link>
          <span class="mx-2">›</span>
          <router-link to="/partner/customers" class="hover:underline">Customers</router-link>
          <span class="mx-2 tm-accent">›
            {{ customerName }}</span>
        </div>
        <!--Page Title-->
        <h2>Create New Outbound Availability Campaign</h2>
        <hr class="h2" />
      </div>

      <div class="form-wrapper ">
        <form
          class="campaign-form campaign-form v-card v-theme--light v-card--density-default elevation-2 v-card--variant-elevated"
          @submit.prevent="handleSubmit">
          <h3>General Settings</h3>
          <div class="form-row inline-row">
            <div class="form-group spaced">
              <label>State<span class="required">*</span></label>
              <select id="state-select" v-model="selectedState">
                <option v-for="state in australianStates" :key="state.value" :value="state.value">{{ state.text }}
                </option>
              </select>
            </div>
            <div class="form-group spaced">
              <label>Campaign Type<span class="required">*</span></label>
              <select required v-model="data.campaignType">
                <option value="" disabled>Select campaign type</option>
                <option value="Truck Availability">Truck Availability</option>
                <option value="Plant Availability">Plant Availability</option>
              </select>
            </div>
          </div>

          <div class="form-row inline-row">
            <div class="form-group spaced">
              <label>AI Agent <span class="required">*</span></label>
              <input type="text" disabled v-model="data.botName" />
            </div>
            <div class="form-group spaced">
              <label>Campaign Name
                <span class="required">*</span>&nbsp;&nbsp;
                <font-awesome-icon class="info-icon" icon="fa-circle-info" v-b-tooltip.hover
                  title="Campaign name is auto-generated using the selected state, campaign type and current date." />
              </label>
              <input type="text" required v-model="data.campaignName" readonly />
            </div>
          </div>

          <hr class="form-divider" />

          <!-- ============ SCHEDULER SETTINGS ============ -->

          <h3>Campaign Schedule Settings</h3>
          <div class="form-row inline-row">
            <div class="form-group spaced">
              <label>Time Zone<span class="required">*</span></label>
              <select required v-model="timeZone">
                <option value="Australia/Sydney">(UTC+10) Australia/Sydney</option>
                <option value="Australia/Darwin">(UTC+9:30) Australia/Darwin</option>
                <option value="Asia/Singapore">(UTC+8) Asia/Singapore</option>
                <option value="Pacific/Auckland">(UTC+12) New Zealand</option>
                <option value="Asia/Bangkok">(UTC+7) Asia/Bangkok</option>
              </select>
            </div>
          </div>

          <div class="form-row inline-row">
            <div class="form-group spaced">
              <label>Start Date
                <span class="required">*</span>&nbsp;&nbsp;
                <font-awesome-icon class="info-icon" icon="fa-circle-info" v-b-tooltip.hover
                  title="The start date is set to the current date by default." />
              </label>
              <input type="date" required v-model="data.beginningDate" />
            </div>
            <div class="form-group spaced">
              <label>End Date
                <span class="required">*</span>&nbsp;&nbsp;
                <font-awesome-icon class="info-icon" icon="fa-circle-info" v-b-tooltip.hover
                  title="The end date is set to the same day as the start date by default." />
              </label>
              <input type="date" required v-model="data.endingDate" />
            </div>
          </div>


          <div class="form-row inline-row">
            <div class="form-group spaced">
              <label>Start Time <span class="required">*</span>&nbsp;&nbsp;
                <font-awesome-icon class="info-icon" icon="fa-circle-info" v-b-tooltip.hover
                  title="Start time is set to the current time by default." />
              </label>
              <input type="time" v-model="data.startLocalTime" />
            </div>
            <div class="form-group spaced">
              <label>End Time
                <span class="required">*</span>&nbsp;&nbsp;
                <font-awesome-icon class="info-icon" icon="fa-circle-info" v-b-tooltip.hover
                  title="End time must be greater than the start time and the current time, in the selected timezone." />
              </label>
              <div class="input-with-note">
                <input type="time" required v-model="data.stopLocalTime" :min="minStopTime" />
                <small class="input-note">Default is one hour after start time. Adjust if needed.</small>
              </div>
            </div>
          </div>
          <hr class="form-divider" />

          <!-- ============ TARGET LISTS & CALLER DATA (improved) ============ -->
          <h3>Requirement Details and Call List</h3>
          <!-- Call list picker -->
          <div>

            <label class="tc-title">Contractor List(s) <span class="required">*</span>
              <button
                type="button"
                class="call-list-action"
                :disabled="isLoadingCallLists || availableCallLists.length === 0"
                @click.prevent="selectAllCallLists">
                {{ availableCallLists.length > 0 && selectedCount === availableCallLists.length ? 'Deselect all' : 'Select all' }}
              </button>
              <span class="call-list-count" :class="{ 'is-empty': selectedCount === 0 }">
                {{ selectedCount }} SELECTED
              </span>
            </label>
            <!-- <p class="tc-subtitle">Choose one or more target call lists to include in this campaign.</p> -->

            <p v-if="isLoadingCallLists" class="call-list-message">Loading contractor lists...</p>
            <p v-else-if="callListError" class="call-list-message call-list-error" role="alert">{{ callListError }}</p>
            <p v-else-if="availableCallLists.length === 0" class="call-list-message">No contractor lists are available.</p>

            <div class="checkbox-list checkbox-list-grid">
              <label v-for="list in availableCallLists" :key="list.id" class="call-list-item"
                :class="{ selected: isSelected(list.id) }">
                <span class="call-item-checkbox">
                  <input type="checkbox" :checked="isSelected(list.id)" @change="toggleCallList(list.id)" />
                </span>
                <span class="call-list-item-icon" aria-hidden="true">
                  <svg width="18" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 7h13v7H3z" stroke="#E45F2B" stroke-width="1.5" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path d="M16 10h4l1 2v3" stroke="#E45F2B" stroke-width="1.5" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <circle cx="7" cy="18" r="2" stroke="#E45F2B" stroke-width="1.5" />
                    <circle cx="18" cy="18" r="2" stroke="#E45F2B" stroke-width="1.5" />
                  </svg>
                </span>
                <span class="call-list-item-text">
                  <span class="call-list-item-title">{{ list.name }}</span>
                  <span class="call-list-item-subtitle">{{ list.subtitle }}</span>
                </span>
              </label>
            </div>
          </div>



          <!-- Equipment type -->
          <div class="tc-block">
            <div class="form-row inline-row">
              <div class="form-group spaced">
                <label>Equipment Type(s) <span class="required">*</span></label>
                <div class="equipment-dropdown">
                  <div class="equipment-input" :class="{ open: equipmentDropdownOpen }"
                    @click.stop="toggleEquipmentDropdown">
                    <span v-if="selectedEquipmentTypes.length === 0" class="placeholder">Select equipment types</span>
                    <span v-else class="equipment-chips">
                      <span v-for="eq in selectedEquipmentTypes" :key="eq" class="equipment-chip">{{ eq }}</span>
                    </span>
                    <span class="chev">▾</span>
                  </div>
                  <div class="equipment-menu" v-if="equipmentDropdownOpen">
                    <label v-for="eq in availableEquipmentTypes" :key="eq" class="equipment-option">
                      <input type="checkbox" :checked="isEquipmentSelected(eq)"
                        @change.stop="toggleEquipmentType(eq)" />
                      <span>{{ eq }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>


          </div>

          <!-- Date / Time / Location / Notes — aligned as one grid -->

          <div class="tc-datetime-grid">
            <div class="form-row inline-row">
              <div class="form-group spaced">

                <label class="tc-title">Date<span class="required">*</span></label>
                <input type="date" v-model="metaDate" />
              </div>
            </div>
            <div class="form-row inline-row">
              <div class="form-group spaced">
                <label class="tc-title">Time<span class="required">*</span></label>
                <input type="time" v-model="metaTime" />
              </div>
            </div>

            <div class="form-row inline-row">
              <div class="form-group spaced">
                <label class="tc-title">Location<span class="required">*</span></label>
                <input type="text" v-model="location" placeholder="E.g. Southport" />
              </div>
            </div>
            <div class="form-row inline-row">
              <div class="form-group spaced">
                <label class="tc-title">Notes</label>
                <textarea v-model="notes"
                  placeholder="Special instructions or requirements. E.g. Must be inducted at Boral Quarry"
                  rows="2"></textarea>
              </div>
            </div>
          </div>

          <!-- ============ END TARGET LISTS & CALLER DATA ============ -->


          <p class="customization-note">
            For additional options and customizations, please email us at
            <a href="mailto:support@trimate.ai">support@trimate.ai</a>.
          </p>
          <button type="submit" class="save-btn" :disabled="isSubmitting">
            <span class="button-spinner" v-if="isSubmitting"></span>
            <span>{{ isSubmitting ? 'Saving...' : 'SAVE' }}</span>
          </button>

        </form>
      </div>
    </div>
  </div>
</template>

<script>
import SideBar from './SideBar.vue'
import PartnerBreadcrumbs from './PartnerBreadcrumbs.vue'

export default {
  name: 'FleetOutboundCampaign',
  components: {
    SideBar,
    PartnerBreadcrumbs
  },
  data() {
    return {
      selectedState: 'All',
      australianStates: [
        { value: 'All', text: 'All States' },
        { value: 'NSW', text: 'New South Wales' },
        { value: 'QLD', text: 'Queensland' },
        { value: 'VIC', text: 'Victoria' }
      ],
      data: {
        botName: 'Outbound Availability',
        campaignType: '',
        campaignName: '',
        startLocalTime: '',
        stopLocalTime: '',
        beginningDate: '',
        endingDate: ''
      },
      timeZone: 'Australia/Sydney',
      customerGUID: '',
      customerName: '',
      botID: '24513573',
      defaultBot: { botID: '24513573', botName: 'Outbound Availability' },
      vicBot: { botID: '24513575', botName: 'VIC - Outbound Availability' },
      // Contractor target lists are loaded from the customer's target-lists API.
      availableCallLists: [],
      targetCallLists: [],
      isLoadingCallLists: false,
      callListError: '',
      // equipment multiselect
      availableEquipmentTypes: [
        "Articulated Dump Truck",
        "Compactor",
        "Crusher",
        "Dozer",
        "Drott Loader",
        "Excavator",
        "Excavator Positrack Combo",
        "Excavator Skid Steer Combo",
        "Grader",
        "PBS Truck & Quad",
        "PBS Truck & Trailer",
        "Positrack Loader",
        "Road Train",
        "Roller",
        "Screener",
        "Semi 19m Stag",
        "Semi End Tipper",
        "Semi Live Floor",
        "Semi Side Tipper",
        "Skid Steer Loader",
        "Street Sweeper",
        "Tandem Tipper",
        "Truck & Quad",
        "Truck & Trailer",
        "Twin Steer Tandem",
        "Twin Steer Tri-Drive",
        "Water Truck",
        "Wheel Loader"
      ],
      selectedEquipmentTypes: [],
      equipmentDropdownOpen: false,
      location: '',
      notes: '',
      metaDate: '',
      metaTime: '',
      isSubmitting: false
    }
  },
  mounted() {
    this.setCampaignDates();
    this.updateCampaignTimes();
    this.customerGUID = (localStorage.getItem('customerGUID') || '').toUpperCase();
    this.customerName = localStorage.getItem('customerName') || '';
    if (!this.customerGUID) {
      this.$router.push('/partner/dashboard');
      return;
    }
    this.fetchAvailableCallLists();
  },
  watch: {
    timeZone() {
      this.updateCampaignTimes();
      this.updateCampaignName();
    },
    selectedState(newVal) {
      this.updateCampaignName();
      this.updateBotForState(newVal);
    },
    'data.startLocalTime'() {
      this.updateCampaignName();
    },
    'data.campaignType'() {
      this.updateCampaignName();
    }
  },
  computed: {
    minStopTime() {
      return this.data.startLocalTime;
    },
    selectedCount() {
      return this.targetCallLists.length;
    }
  },
  methods: {
    async fetchAvailableCallLists() {
      this.isLoadingCallLists = true;
      this.callListError = '';

      try {
        const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS;
        const url = `${baseUrl}/customers/${this.customerGUID}/campaign/fph-availability/target-lists`;
        const response = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
          const errorMessage = await response.text().catch(() => '');
          throw new Error(errorMessage || `Unable to load contractor lists (${response.status}).`);
        }

        const result = await response.json();
        if (
          result.success !== true ||
          !Array.isArray(result.files) ||
          result.files.some(file => !file || typeof file.file !== 'string')
        ) {
          throw new Error('The contractor list response was not in the expected format.');
        }

        this.availableCallLists = result.files.map(file => {
          const baseName = file.file.replace(/\.csv$/i, '');
          const area = file.area || '';
          const equipment = file.equipment_type || '';
          return {
            id: file.file,
            fileName: file.file,
            name: area && equipment ? `${area} - ${equipment}` : baseName.replace(/_/g, ' '),
            subtitle: area
          };
        });
      } catch (error) {
        this.callListError = error.message || 'Unable to load contractor lists.';
        console.error('[Fleet campaign] Failed to load contractor lists', error);
      } finally {
        this.isLoadingCallLists = false;
      }
    },
    updateBotForState(state) {
      const bot = state === 'VIC' ? this.vicBot : this.defaultBot;
      this.botID = bot.botID;
      this.data.botName = bot.botName;
    },
    updateCampaignName() {
      if (!this.data.campaignType) {
        this.data.campaignName = '';
        return;
      }

      const stateCode = this.selectedState && this.selectedState !== 'All' ? this.selectedState : 'ALL';
      const formattedTime = this.formatTimeForName(this.data.startLocalTime);
      const baseName = this.data.campaignType.replace(/\s+/g, '_');
      const suffix = this.getDateSuffix();
      this.data.campaignName = `${stateCode}_${baseName}_${suffix}_${formattedTime}`;
    },
    formatTimeForName(timeStr) {
      if (!timeStr) return '12:00';
      const [hourStr, minuteStr] = timeStr.split(':');
      let hour = parseInt(hourStr, 10);
      const minute = (minuteStr || '00').padStart(2, '0');
      if (Number.isNaN(hour)) return '12:00';
      const hourFormatted = String(hour).padStart(2, '0');
      return `${hourFormatted}:${minute}`;
    },
    getDateSuffix() {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[now.getMonth()];
      const year = now.getFullYear();
      return `${day}${month}${year}`;
    },
    setCampaignDates() {
      const today = new Date();
      const formattedDate = this.formatDate(today, this.timeZone);
      this.data.beginningDate = formattedDate;
      this.data.endingDate = formattedDate;
    },
    updateCampaignTimes() {
      const now = new Date();
      this.data.startLocalTime = this.formatTime(now, this.timeZone);
      const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
      this.data.stopLocalTime = this.formatTime(oneHourLater, this.timeZone);
    },
    // Target list selection helpers
    isSelected(listId) {
      return this.targetCallLists.includes(listId);
    },
    toggleCallList(listId) {
      const idx = this.targetCallLists.indexOf(listId);
      if (idx === -1) this.targetCallLists.push(listId);
      else this.targetCallLists.splice(idx, 1);
    },
    selectAllCallLists() {
      if (this.isLoadingCallLists || this.availableCallLists.length === 0) return;
      if (this.targetCallLists.length === this.availableCallLists.length) {
        this.targetCallLists = [];
      } else {
        this.targetCallLists = this.availableCallLists.map(l => l.id);
      }
    },
    toggleEquipmentDropdown() {
      this.equipmentDropdownOpen = !this.equipmentDropdownOpen;
    },
    isEquipmentSelected(eq) {
      return this.selectedEquipmentTypes.includes(eq);
    },
    toggleEquipmentType(eq) {
      const idx = this.selectedEquipmentTypes.indexOf(eq);
      if (idx === -1) this.selectedEquipmentTypes.push(eq);
      else this.selectedEquipmentTypes.splice(idx, 1);
    },
    formatDate(date, timeZone) {
      const options = { year: 'numeric', month: '2-digit', day: '2-digit', timeZone };
      const formatted = new Intl.DateTimeFormat('en-CA', options).format(date);
      return formatted;
    },
    formatTime(date, timeZone) {
      const options = { hour: '2-digit', minute: '2-digit', hour12: false, timeZone };
      return new Intl.DateTimeFormat('en-GB', options).format(date);
    },
    buildReqDateTime() {
      if (!this.metaDate || !this.metaTime) return '';
      const [year, month, day] = this.metaDate.split('-'); // input type=date -> YYYY-MM-DD
      return `${day}-${month}-${year} ${this.metaTime}`;
    },
    getSelectedCallListNames() {
      return this.availableCallLists
        .filter(l => this.targetCallLists.includes(l.id))
        .map(l => l.fileName);
    },
    async handleSubmit() {
      if (!this.data.campaignType) {
        alert('Please select a campaign type.');
        return;
      }

      if (this.data.stopLocalTime <= this.data.startLocalTime) {
        alert('End Time must be greater than Start Time.');
        return;
      }

      if (this.targetCallLists.length === 0) {
        alert('Please select at least one call list.');
        return;
      }

      if (this.selectedEquipmentTypes.length === 0) {
        alert('Please select at least one equipment type.');
        return;
      }

      try {
        this.isSubmitting = true;

        const payload = {
          data: {
            botID: this.botID,
            campaignName: this.data.campaignName,
            schedulerSettings: {
              timeZone: this.timeZone,
              beginningDate: this.data.beginningDate,
              endingDate: this.data.endingDate,
              startLocalTime: this.data.startLocalTime,
              stopLocalTime: this.data.stopLocalTime
            },
            targetLists: this.getSelectedCallListNames(),
            targetMetadata: {
              reqEquipmentTypes: this.selectedEquipmentTypes.join(', '),
              reqDateTime: this.buildReqDateTime(),
              reqLocation: this.location,
              reqNotes: this.notes
            }
          }
        };

        const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS;
        const requestUrl = `${baseUrl}/customers/${this.customerGUID}/campaign/create/fph-availability`;

        const response = await fetch(requestUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });


        if (!response.ok) {
          const errBody = await response.text().catch(() => '');
          console.error('FleetOutboundCampaign: API error response:', errBody);
          throw new Error(errBody || `Request failed with status ${response.status}`);
        }

        const responseData = await response.json().catch(() => null);
        if (!responseData || responseData.success !== true) {
          throw new Error(responseData?.message || 'The campaign could not be created.');
        }

        // Return to the customer page with the outbound campaigns tab selected.

        try {
          await this.$router.push({
            path: '/partner/customer',
            query: {
              activeTab: 'outboundCampaign',
              campaignCreated: 'true'
            }
          });
        } catch (navError) {
          console.error('FleetOutboundCampaign: router.push failed:', navError);
        }
      } catch (error) {
        console.error('Error creating fleet campaign:', error);
        alert('Something went wrong while creating the campaign. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
</script>

<style scoped>
/* tm stands for Trimate */
.tm-grey-text>span,
.tm-grey-text>a {
  color: #788e90
}

.tm-accent {
  color: #e45f2b !important
}

.tm-breadcrumbs {
  text-align: left;
}

.tm-breadcrumbs>a,
.tm-breadcrumbs>span {
  font-size: 18px !important;
  font-family: Montserrat, sans-serif;
  text-decoration: none;
}

.form-wrapper {
  padding: 0 1rem;
  width: 100%;
}

.campaign-form {
  font-family: 'Montserrat', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 95%;
  margin: 40px 20px 20px 20px;
  padding: 20px;
}

.campaign-form h3 {
  font-weight: 500;
  margin: 0;
  font-size: 1.1rem;
  text-transform: uppercase;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 1.5rem 2.5rem;
  justify-content: flex-start;
  margin-left: 0;
  padding-left: 0;
}

.form-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 280px;
}

.form-group label {
  width: 160px;
  text-align: left;
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #374151;
}

.form-group.spaced {
  margin-right: 0;
}

.form-divider {
  width: 100%;
  margin: 0;
  border: none;
  border-top: 1px solid #ccc;
}

input,
select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.875rem;
  font-family: inherit;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
  width: 100%;
  max-width: 330px;
}

input:hover,
select:hover {
  border-color: #E45F2B;
}

input:focus,
select:focus {
  outline: none;
  border-color: #E45F2B;
  box-shadow: 0 0 0 3px rgba(228, 95, 43, 0.12);
}

input:disabled,
select:disabled {
  background: #F9FAFB;
  color: #9CA3AF;
  cursor: not-allowed;
  border-color: #E5E7EB;
}

input:disabled:hover,
select:disabled:hover {
  border-color: #E5E7EB;
}

.save-btn {
  align-self: flex-start;
  cursor: pointer;
  border: none;
  border-radius: 20px;
  color: #FFFFFF;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 34px;
  padding: 0 1.25rem;
  text-align: center;
  text-transform: uppercase;
  background-color: #E45F2B;
}

.save-btn:hover {
  background-color: #000000;
}

.save-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.button-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 0.5rem;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spinner-rotate 0.8s linear infinite;
  vertical-align: middle;
}

@keyframes spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}

.input-note {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 5px;
}

.customization-note {
  clear: both;
  font-size: 14px;
  color: #555;
  font-style: italic;
}

.customization-note a {
  color: #e45f2b;
  text-decoration: underline;
}

.page-title {
  font-size: 1.25rem;
  margin: 0 0 1rem 0;
  font-weight: 600;
  background-color: #fafafa;
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  padding: 10px;
}

.input-with-note {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.info-icon {
  color: #FE602F;
  cursor: default;
}

.required {
  color: #E45F2B;
}

/* ============================================================
   TARGET LISTS & CALLER DATA — improved styles
   ============================================================ */

.tc-block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.tc-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  width: auto;
}

.checkbox-list.checkbox-list-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 10px;
}

.call-list-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: box-shadow .15s ease, transform .12s ease, border-color .12s ease;
}

.call-list-item:hover {
  box-shadow: 0 6px 16px rgba(14, 21, 47, 0.08);
  border-color: #E45F2B;
  transform: translateY(-1px);
}

.call-list-item.selected {
  border-color: #E45F2B;
  background: #FFF7F3;
}

.call-item-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #E45F2B;
  margin-top: 2px;
}

.call-list-item-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #FFF4EE;
}

.call-list-item-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.call-list-item-title {
  font-size: 14px;
  font-weight: 600;
  color: #1F2937;

}

.call-list-item-subtitle {
  font-size: 12px;
  color: #6B7280;
}

.call-list-action {
  border: 1px solid rgba(228, 95, 43, 0.35);
  background: #fff;
  color: #E45F2B;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
  min-width: none;
}

.call-list-action:hover {
  background: #E45F2B;
  color: #fff;
}

.call-list-action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.call-list-message {
  margin: 10px 0;
  color: #6B7280;
  font-size: 13px;
}

.call-list-error {
  color: #a52822;
}

.call-list-count {
  background: #E45F2B;
  color: #fff;
  padding: 9px 14px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.call-list-count.is-empty {
  background: #9CA3AF;
}

.equipment-dropdown {
  position: relative;
  width: 100%;
  max-width: 700px;
}

.equipment-input {
  border: 1px solid #ccc;
  padding: 8px 12px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  background: #fff;
  min-height: 40px;
  transition: border-color 0.15s ease;
}

.equipment-input.open,
.equipment-input:hover {
  border-color: #E45F2B;
}

.equipment-input .placeholder {
  color: #9CA3AF;
  font-size: 0.875rem;
}

.equipment-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.equipment-chip {
  background: #FFF4EE;
  color: #E45F2B;
  border: 1px solid #FBD9C9;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
}

.equipment-input .chev {
  color: #6B7280;
  flex-shrink: 0;
}

.equipment-menu {
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  padding: 8px;
  box-shadow: 0 10px 30px rgba(14, 21, 47, 0.1);
  z-index: 40;
  width: 100%;
  min-width: 520px;
  max-height: 320px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 2px;
}

.equipment-menu>label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.70rem;
  line-height: 1.2;
  font-weight: 400;
}

.equipment-option span {
  overflow-wrap: break-word;
}

.equipment-option:hover {
  background: #FFF7F3;
}

.equipment-option input {
  width: 16px;
  height: 16px;
  accent-color: #E45F2B;
}

.tc-datetime-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 2.5rem;
  row-gap: 1.5rem;
}

textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.75rem;
  font-family: inherit;
  width: 100%;
  max-width: 330px;
  transition: border-color 0.15s ease;
  resize: vertical;
}

textarea:hover {
  border-color: #E45F2B;
}

textarea:focus {
  outline: none;
  border-color: #E45F2B;
  box-shadow: 0 0 0 3px rgba(228, 95, 43, 0.12);
}

@media (max-width: 640px) {
  .tc-datetime-grid {
    grid-template-columns: 1fr;
  }

  .equipment-menu {
    grid-template-columns: 1fr;
    min-width: 0;
  }
}
</style>